import { call, fork, put, takeEvery, takeLatest } from "redux-saga/effects"
import { get, post, getVendorsAxios, axiosPut } from "helpers/api_helper"
import {
  apiError,
  loginSuccess,
  logoutUserSuccess,
  getFeaturesAvailableSuccess,
  getClientConfigSuccess,
  getUserPermissionsSuccess,
  set2FaToken,
  resend2FaCodeSuccess,
} from "./actions"
import { getVendors, getVendorsSuccess } from "store/clients/actions"
import {
  LOGIN_USER,
  LOGOUT_USER,
  GET_FEATURES_AVAILABLE,
  GET_CLIENT_CONFIG,
  ENSURE_AUTH_USER,
  GET_USER_PERMISSIONS,
  SWITCH_CLIENT,
  RESET_PASSWORD,
  VALIDATE_2FA,
  RESEND_2FA_CODE,
} from "store/auth/login/actionTypes"
import { URL_PATHS, LOCAL_STORAGE_TAGS } from "constants/index"
import * as url from "helpers/url_helper"
import { showToast } from "utils"
import { getDownloads } from "store/actions"

function* ensureAuthUserSaga({}) {
  let auth_user = yield JSON.parse(
    localStorage.getItem(LOCAL_STORAGE_TAGS.AUTH_USER)
  )
  let clients = yield JSON.parse(
    localStorage.getItem(LOCAL_STORAGE_TAGS.CLIENTS_TAG)
  )
  let features = yield JSON.parse(
    localStorage.getItem(LOCAL_STORAGE_TAGS.FEATURES_TAG)
  )
  let client_config = yield JSON.parse(
    localStorage.getItem(LOCAL_STORAGE_TAGS.CONFIG_TAG)
  )
  let permissions = yield JSON.parse(
    localStorage.getItem(LOCAL_STORAGE_TAGS.USER_PERMISSIONS_TAG)
  )
  let vendorId = yield localStorage.getItem(LOCAL_STORAGE_TAGS.VENDOR_ID_TAG)

  yield put(loginSuccess(auth_user, vendorId))
  yield put(getVendorsSuccess(clients))
  yield put(getFeaturesAvailableSuccess(features))
  yield put(getClientConfigSuccess(client_config))
  yield put(getUserPermissionsSuccess(permissions))
  if (!!auth_user) {
    yield put(getDownloads())
  }

  yield fork(getFeaturesAvailableSaga, { })
}

function* validate2Fa({ payload: { params, history } }) {
  try {
    const response = yield post(url.VALIDATE_2FA_URL, params)
    yield localStorage.setItem(
      LOCAL_STORAGE_TAGS.AUTH_USER,
      JSON.stringify(response)
    )
    yield localStorage.setItem(
      LOCAL_STORAGE_TAGS.VENDOR_ID_TAG,
      response.vendor_id
    )

    yield put(getVendors())
    yield getFeaturesAvailableSaga({})
    yield getUserPermissionsSaga({})
    yield getClientConfigSaga({
      payload: {
        vendor_id: response.vendor_id,
      },
    })
    yield put(getDownloads())
    yield put(loginSuccess(response, response.vendor_id))

    history.push(URL_PATHS.DASHBOARD)
  } catch ({ response }) {
    yield put(apiError(response))
    const tokenError = response?.data?.error?.token
      ? response?.data?.error?.token
      : null
    const codeError = response?.data?.error?.code
      ? response?.data?.error?.code
      : null
    if (tokenError || codeError) {
      showToast(
        "error",
        `${tokenError ? tokenError : ""}` + `${codeError ? codeError : ""}`,
        "Error"
      )
    } else if (response?.data?.error) {
      showToast("error", response?.data?.error, "Error")
    }
  }
}

function* resend2FaCode({ payload: params }) {
  try {
    const response = yield post(url.RESEND_2FA_URL, params)
    yield put(resend2FaCodeSuccess(response))
    showToast(
      "success",
      "A new code was sent to your email address!",
      "Success"
    )
  } catch ({ response }) {
    yield put(apiError(response))
    showToast("error", response?.data?.error || "Error", "Error")
  }
}

function* loginUser({ payload: { user, history } }) {
  try {
    const response = yield post(url.POST_LOGIN, {
      email: user.email,
      password: user.password,
    })
    yield put(set2FaToken(response?.token))
    history.push(URL_PATHS.TWO_FA)
  } catch ({ response }) {
    yield put(apiError(response))
    showToast("error", response?.data?.error || "Error", "Error")
  }
}

function* logoutUser({ payload: { history } }) {
  try {
    let user = localStorage.getItem(LOCAL_STORAGE_TAGS.AUTH_USER)
    const response = yield post(url.POST_LOGOUT, JSON.parse(user))

    yield localStorage.removeItem(LOCAL_STORAGE_TAGS.AUTH_USER)
    yield localStorage.removeItem(LOCAL_STORAGE_TAGS.VENDOR_ID_TAG)
    yield localStorage.removeItem(LOCAL_STORAGE_TAGS.CLIENTS_TAG)
    yield localStorage.removeItem(LOCAL_STORAGE_TAGS.FEATURES_TAG)
    yield localStorage.removeItem(LOCAL_STORAGE_TAGS.CONFIG_TAG)
    yield localStorage.removeItem(LOCAL_STORAGE_TAGS.USER_PERMISSIONS_TAG)

    yield put(logoutUserSuccess(response))
    history.push(URL_PATHS.LOGIN)
  } catch (error) {
    yield put(apiError(error))
  }
}

function* getFeaturesAvailableSaga({ payload: params }) {
  try {
    const response = yield get(`${url.GET_FEATURES_AVAILABLE}`, { params })
    yield put(getFeaturesAvailableSuccess(response))
    yield localStorage.setItem(
      LOCAL_STORAGE_TAGS.FEATURES_TAG,
      JSON.stringify(response)
    )
  } catch (error) {
    yield put(apiError(error))
  }
}

function* getClientConfigSaga({ payload: params }) {
  try {
    const response = yield getVendorsAxios(
      `${url.GET_VENDOR_URL}/${params.vendor_id}${url.GET_CLIENT_CONFIG}`,
      { params }
    )
    yield put(getClientConfigSuccess(response))
    yield localStorage.setItem(
      LOCAL_STORAGE_TAGS.CONFIG_TAG,
      JSON.stringify(response)
    )
  } catch (error) {
    yield put(apiError(error))
  }
}

function* getUserPermissionsSaga({ payload: params }) {
  try {
    const response = yield get(`${url.GET_USER_PERMISSIONS}`, { params })
    yield put(getUserPermissionsSuccess(response))
    yield localStorage.setItem(
      LOCAL_STORAGE_TAGS.USER_PERMISSIONS_TAG,
      JSON.stringify(response)
    )
  } catch (error) {
    yield put(apiError(error))
  }
}

function* switchClientSaga({ payload: { clientId, history } }) {
  try {
    yield call(() =>
      localStorage.setItem(LOCAL_STORAGE_TAGS.VENDOR_ID_TAG, clientId)
    )
    const response = yield call(() => get(`${url.SWITCH_USER_URL}`))
    let vendor_id = localStorage.getItem(LOCAL_STORAGE_TAGS.VENDOR_ID_TAG)
    yield localStorage.setItem(
      LOCAL_STORAGE_TAGS.AUTH_USER,
      JSON.stringify(response)
    )

    yield put(loginSuccess(response, vendor_id))
    yield put(getVendors())
    yield getFeaturesAvailableSaga({})
    yield getUserPermissionsSaga({})
    yield getClientConfigSaga({
      payload: {
        vendor_id: vendor_id,
      },
    })
    yield put(getDownloads())
    if (history.location.pathname === URL_PATHS.DASHBOARD) {
      history.go(0)
    } else {
      history.push(URL_PATHS.DASHBOARD)
    }
  } catch (error) {
    yield put(apiError(error))
  }
}

function* resetPasswordSaga({ payload: { user, history } }) {
  try {
    const response = yield axiosPut(
      url.POST_USER_RESET,
      {
        password: user.password,
      },
      {
        headers: {
          ["Authorization"]: `Bearer ${user?.api_token}`,
        },
      }
    )

    history.push(URL_PATHS.LOGIN)
  } catch (error) {
    yield put(apiError(error))
  }
}

function* authSaga() {
  yield takeEvery(LOGIN_USER, loginUser)
  yield takeEvery(ENSURE_AUTH_USER, ensureAuthUserSaga)
  yield takeEvery(RESET_PASSWORD, resetPasswordSaga)
  yield takeEvery(LOGOUT_USER, logoutUser)
  yield takeEvery(GET_FEATURES_AVAILABLE, getFeaturesAvailableSaga)
  yield takeEvery(GET_CLIENT_CONFIG, getClientConfigSaga)
  yield takeEvery(GET_USER_PERMISSIONS, getUserPermissionsSaga)
  yield takeEvery(SWITCH_CLIENT, switchClientSaga)
  yield takeEvery(VALIDATE_2FA, validate2Fa)
  yield takeEvery(RESEND_2FA_CODE, resend2FaCode)
}

export default authSaga
